/** Pregnancy dating from the due date. Pure functions — no DOM, no storage. */

export const DAY_MS = 86_400_000;
export const FULL_TERM_DAYS = 280;
/** Daily counting is recommended from week 28 (third trimester). */
export const COUNTING_START_WEEK = 28;

export interface PregnancyInfo {
  daysLeft: number;      // negative = past due date
  week: number;          // completed weeks
  day: number;           // 0–6
  progress: number;      // 0–1
  trimester: 1 | 2 | 3;
}

/** Parse YYYY-MM-DD as a LOCAL date (new Date('2026-01-01') would be UTC and shift a day in some zones). */
export function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) throw new Error(`invalid date: ${iso}`);
  return new Date(y, m - 1, d);
}

export function toLocalISODate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function startOfLocalDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function pregnancyInfo(dueDate: string, now: number = Date.now()): PregnancyInfo {
  const due = parseLocalDate(dueDate).getTime();
  const daysLeft = Math.round((due - startOfLocalDay(now)) / DAY_MS);
  const daysIn = FULL_TERM_DAYS - daysLeft;
  return {
    daysLeft,
    week: Math.floor(daysIn / 7),
    day: ((daysIn % 7) + 7) % 7,
    progress: Math.min(1, Math.max(0, daysIn / FULL_TERM_DAYS)),
    trimester: daysIn < 98 ? 1 : daysIn < 196 ? 2 : 3
  };
}

export type DueDateError = 'empty' | 'too_far' | 'too_past';

/** Accept due dates up to 40 weeks ahead and up to 2 weeks past. */
export function validateDueDate(dueDate: string, now: number = Date.now()): DueDateError | null {
  if (!dueDate) return 'empty';
  const { daysLeft } = pregnancyInfo(dueDate, now);
  if (daysLeft > FULL_TERM_DAYS) return 'too_far';
  if (daysLeft < -14) return 'too_past';
  return null;
}
